import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  applyDocumentLocale,
  getSavedLocale,
  LOCALE_STORAGE_KEY,
  LOCALES,
  type LocaleKey
} from '@/plugins/i18n'

export const useLocale = () => {
  const { locale } = useI18n()

  const changeLocale = (value: LocaleKey) => {
    const next = value === 'ar' || value === 'en' ? value : 'en'
    if (next === locale.value) return

    // SSR guard
    if (typeof document === 'undefined') {
      locale.value = next
      applyDocumentLocale(next)
      return
    }

    const el = document.documentElement
    // Prevent re-entry while a transition is running
    if (el.classList.contains('locale-switching')) return

    // Fade out
    el.classList.add('locale-switching')

    setTimeout(() => {
      // Apply locale change at the midpoint (body is invisible)
      locale.value = next
      if (typeof window !== 'undefined') {
        localStorage.setItem(LOCALE_STORAGE_KEY, next)
      }
      applyDocumentLocale(next)

      // Fade in on the next frame
      requestAnimationFrame(() => {
        el.classList.remove('locale-switching')
      })
    }, 300)
  }

  const currentLocale = computed<LocaleKey>({
    get: () => {
      const value = locale.value as LocaleKey
      return value === 'ar' || value === 'en' ? value : 'en'
    },
    set: (value) => {
      changeLocale(value)
    }
  })

  const dir = computed(() => LOCALES[currentLocale.value]?.dir ?? 'ltr')
  const isRTL = computed(() => dir.value === 'rtl')

  const toggleLocale = () => {
    changeLocale(currentLocale.value === 'ar' ? 'en' : 'ar')
  }

  // Keep document attributes + storage in sync on initial load and changes.
  watch(
    () => locale.value,
    (value) => {
      const next = value === 'ar' || value === 'en' ? value : 'en'
      if (typeof window !== 'undefined') {
        localStorage.setItem(LOCALE_STORAGE_KEY, next)
      }
      applyDocumentLocale(next)
    },
    { immediate: true }
  )

  // Ensure i18n starts with the saved locale if loaded later.
  if (locale.value !== getSavedLocale()) {
    locale.value = getSavedLocale()
  }

  return {
    currentLocale,
    isRTL,
    dir,
    changeLocale,
    toggleLocale
  }
}
